/**
 * PerformanceScoreBadge Component
 *
 * Displays a performance score with color coding based on value.
 * Uses the standard score ranges: Excellent (4.5+), Above Avg (3.5-4.5), Adequate (2.5-3.5), Needs Improvement (<2.5)
 */
import React from 'react'
import { cn } from '@/lib/utils'

interface PerformanceScoreBadgeProps {
  score: number | null
  showMax?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

function getScoreConfig(score: number | null) {
  if (score === null) {
    return {
      label: 'N/A',
      bgClass: 'bg-brand-gray/20',
      textClass: 'text-brand-gray',
    }
  }
  if (score >= 4.5) {
    return {
      label: 'Excellent',
      bgClass: 'bg-green-500/10',
      textClass: 'text-green-500',
    }
  }
  if (score >= 3.5) {
    return {
      label: 'Above Avg',
      bgClass: 'bg-brand-primary/10',
      textClass: 'text-brand-primary',
    }
  }
  if (score >= 2.5) {
    return {
      label: 'Adequate',
      bgClass: 'bg-yellow-500/10',
      textClass: 'text-yellow-500',
    }
  }
  return {
    label: 'Needs Improv.',
    bgClass: 'bg-red-500/10',
    textClass: 'text-red-500',
  }
}

const sizeClasses = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
}

export function PerformanceScoreBadge({
  score,
  showMax = true,
  size = 'md',
  className,
}: PerformanceScoreBadgeProps) {
  const config = getScoreConfig(score)
  const displayScore = score !== null ? score.toFixed(1) : '—'

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-lg px-2 py-1 font-medium',
        config.bgClass,
        config.textClass,
        sizeClasses[size],
        className
      )}
      title={config.label}
    >
      <span className="font-semibold">{displayScore}</span>
      {showMax && score !== null && <span className="opacity-60">/ 5</span>}
    </span>
  )
}

/**
 * Inline version for compact displays
 */
export function PerformanceScoreInline({
  score,
  className,
}: {
  score: number | null
  className?: string
}) {
  const config = getScoreConfig(score)
  const displayScore = score !== null ? score.toFixed(1) : '—'

  return (
    <span className={cn('font-medium', config.textClass, className)}>
      ★ {displayScore}
    </span>
  )
}
